import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { useSystemTheme } from "@/hooks/useSystemTheme";

import { DemoBanner, Footer, PageBar, Sidebar, SidebarToggle, TopBar } from "@/components/organisms";

import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { NotificationManager } from "@/components/ui/NotificationManager";
import FizurShell from "@/components/organisms/FizurShell";
import { isFizurHost } from "@/utils/fizurHost";
import NoBalanceBanner from "@/components/molecules/NoBalanceBanner";

import { useAppStore, useLoading, useDarkMode, setRouterNavigate } from "@/store/appStore";
import { DEFAULT_PATH, PERSON_REPORT_PAGE, canonicalPathForModuleKey, useActivePageKey } from "@/utils/pageRoutes";
import { DEMO_HIDDEN_PAGES } from "@/utils/demo";
import { useDemoStatus } from "@/hooks/useDemoStatus";
import { cn } from "@/utils";
import apiService from "@/services/api";

// Создаем Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 минут
    },
    mutations: {
      retry: 1,
    },
  },
});

// Страницы грузятся по требованию: раньше все 75 лежали в одном бандле
// (index-*.js весил 2,59 МБ), и переход между модулями упирался не в сеть,
// а в разбор мегабайтов JS — экран оставался пустым по несколько секунд.
// Бочка @/components/pages здесь не годится: импорт из неё тянет всё сразу
// и сводит разбиение на нет, поэтому пути указаны до файла.
const AUViolationsPage = React.lazy(() => import("@/components/pages/compliance/AUViolationsPage"));
const AddressValidationPage = React.lazy(() => import("@/components/pages/validation/AddressValidationPage"));
const AdminInvoicesPage = React.lazy(() => import("@/components/pages/admin/AdminInvoicesPage"));
const AdminSubscriptionsPage = React.lazy(() => import("@/components/pages/admin/AdminSubscriptionsPage"));
const AdministrativeViolationsPage = React.lazy(() => import("@/components/pages/AdministrativeViolationsPage"));
const AlimonyDebtPage = React.lazy(() => import("@/components/pages/compliance/AlimonyDebtPage"));
const ApplicationsListPage = React.lazy(() => import("@/components/pages/ApplicationsListPage"));
const ArbitrationCourtsPage = React.lazy(() => import("@/components/pages/compliance/ArbitrationCourtsPage"));
const BankruptcyPage = React.lazy(() => import("@/components/pages/compliance/BankruptcyPage"));
const BillingSettingsPage = React.lazy(() => import("@/components/pages/admin/BillingSettingsPage"));
const CbrWarningListPage = React.lazy(() => import("@/components/pages/compliance/CbrWarningListPage"));
const ClientRegisterPage = React.lazy(() => import("@/components/pages/ClientRegisterPage"));
const ClientsListPage = React.lazy(() => import("@/components/pages/ClientsListPage"));
const CompanyCheckPage = React.lazy(() => import("@/components/pages/CompanyCheckPage"));
const CompanyReportPage = React.lazy(() => import("@/components/pages/business/CompanyReportPage"));
const CompleteRegistrationPage = React.lazy(() => import("@/components/pages/CompleteRegistrationPage"));
const ComplexCheckReportPage = React.lazy(() => import("@/components/pages/report/ComplexCheckReportPage"));
const CryptoCheckPage = React.lazy(() => import("@/components/pages/CryptoCheckPage"));
const ComplexLegalCheckReportPage = React.lazy(() => import("@/components/pages/report/ComplexLegalCheckReportPage"));
const CorruptionPage = React.lazy(() => import("@/components/pages/compliance/CorruptionPage"));
const CreditRatingPage = React.lazy(() => import("@/components/pages/compliance/CreditRatingPage"));
const CreditScoringPage = React.lazy(() => import("@/components/pages/compliance/CreditScoringPage"));
const DigitalFootprintPage = React.lazy(() => import("@/components/pages/DigitalFootprintPage"));
const DocumentValidationPage = React.lazy(() => import("@/components/pages/validation/DocumentValidationPage"));
const EmailValidationPage = React.lazy(() => import("@/components/pages/validation/EmailValidationPage"));
const ExecutionProceedingsPage = React.lazy(() => import("@/components/pages/compliance/ExecutionProceedingsPage"));
const ExtendedReportPage = React.lazy(() => import("@/components/pages/ExtendedReportPage"));
const FNPPledgesPage = React.lazy(() => import("@/components/pages/compliance/FNPPledgesPage"));
const FizurPage = React.lazy(() => import("@/components/pages/business/FizurPage"));
const FetcherDashboardPage = React.lazy(() => import("@/components/pages/FetcherDashboardPage"));
const FnsAccountSuspensionPage = React.lazy(() => import("@/components/pages/compliance/FnsAccountSuspensionPage"));
const FnsDisqualifiedPage = React.lazy(() => import("@/components/pages/compliance/FnsDisqualifiedPage"));
const FnsMassFoundersPage = React.lazy(() => import("@/components/pages/compliance/FnsMassFoundersPage"));
const FnsMassLeadersPage = React.lazy(() => import("@/components/pages/compliance/FnsMassLeadersPage"));
const FnsSubsidiaryDefendantPage = React.lazy(() => import("@/components/pages/compliance/FnsSubsidiaryDefendantPage"));
const FnsTaxIdVerificationPage = React.lazy(() => import("@/components/pages/verification/FnsTaxIdVerificationPage"));
const ForeignAgentPage = React.lazy(() => import("@/components/pages/compliance/ForeignAgentPage"));
const FsinCriminalWantedPage = React.lazy(() => import("@/components/pages/compliance/FsinCriminalWantedPage"));
const FsspCriminalWantedPage = React.lazy(() => import("@/components/pages/compliance/FsspCriminalWantedPage"));
const FsspDebtorWantedPage = React.lazy(() => import("@/components/pages/compliance/FsspDebtorWantedPage"));
const GeneralCourtsPage = React.lazy(() => import("@/components/pages/compliance/GeneralCourtsPage"));
const HealthDashboardPage = React.lazy(() => import("@/components/pages/HealthDashboardPage"));
const HistoryPage = React.lazy(() => import("@/components/pages/HistoryPage"));
const IPAddressValidationPage = React.lazy(() => import("@/components/pages/validation/IPAddressValidationPage"));
const InterpolRedNoticePage = React.lazy(() => import("@/components/pages/compliance/InterpolRedNoticePage"));
const InterpolUnSanctionsPage = React.lazy(() => import("@/components/pages/compliance/InterpolUnSanctionsPage"));
const InterpolYellowNoticePage = React.lazy(() => import("@/components/pages/compliance/InterpolYellowNoticePage"));
const LoginPage = React.lazy(() => import("@/components/pages/LoginPage"));
const MVDControlledPersonsPage = React.lazy(() => import("@/components/pages/compliance/MVDControlledPersonsPage"));
const ModulesCatalogPage = React.lazy(() => import("@/components/pages/ModulesCatalogPage"));
const GenericModulePage = React.lazy(() => import("@/components/pages/GenericModulePage"));
const MonitoringPage = React.lazy(() => import("@/components/pages/MonitoringPage"));
const MonitoringRunReportPage = React.lazy(() => import("@/components/pages/MonitoringRunReportPage"));
const MonitoringSubjectProfilePage = React.lazy(() => import("@/components/pages/MonitoringSubjectProfilePage"));
const MvdDriverLicenseVerificationPage = React.lazy(
  () => import("@/components/pages/verification/MvdDriverLicenseVerificationPage"),
);
const MvdWantedPage = React.lazy(() => import("@/components/pages/compliance/MvdWantedPage"));
const NameValidationPage = React.lazy(() => import("@/components/pages/validation/NameValidationPage"));
const NotificationsPage = React.lazy(() => import("@/components/pages/NotificationsPage"));
const NotificationsPreferencesPage = React.lazy(() => import("@/components/pages/NotificationsPreferencesPage"));
const ObrnadzorEducationVerificationPage = React.lazy(
  () => import("@/components/pages/verification/ObrnadzorEducationVerificationPage"),
);
const OrganizationBillingPage = React.lazy(() => import("@/components/pages/organization/OrganizationBillingPage"));
const OrganizationCheckoutPage = React.lazy(() => import("@/components/pages/organization/OrganizationCheckoutPage"));
const OrganizationDashboardPage = React.lazy(() => import("@/components/pages/organization/OrganizationDashboardPage"));
const OsintEmailPage = React.lazy(() => import("@/components/pages/digital_footprint/OsintEmailPage"));
const OsintPhonePage = React.lazy(() => import("@/components/pages/digital_footprint/OsintPhonePage"));
const OsintUsernamePage = React.lazy(() => import("@/components/pages/digital_footprint/OsintUsernamePage"));
const OsintDomainPage = React.lazy(() => import("@/components/pages/digital_footprint/OsintDomainPage"));
const OsintIpPage = React.lazy(() => import("@/components/pages/digital_footprint/OsintIpPage"));
const OsintCryptoPage = React.lazy(() => import("@/components/pages/digital_footprint/OsintCryptoPage"));
const PassportVerificationPage = React.lazy(() => import("@/components/pages/verification/PassportVerificationPage"));
const PepScreeningPage = React.lazy(() => import("@/components/pages/compliance/PepScreeningPage"));
const PersonCheckPage = React.lazy(() => import("@/components/pages/PersonCheckPage"));
const PhoneValidationPage = React.lazy(() => import("@/components/pages/validation/PhoneValidationPage"));
const PlatformRiskMatrixPage = React.lazy(() => import("@/components/pages/admin/PlatformRiskMatrixPage"));
const PresentationPage = React.lazy(() => import("@/components/pages/PresentationPage"));
const ResetPasswordPage = React.lazy(() => import("@/components/pages/ResetPasswordPage"));
const SanctionsScreeningPage = React.lazy(() => import("@/components/pages/compliance/SanctionsScreeningPage"));
const SettingsPage = React.lazy(() => import("@/components/pages/SettingsPage"));
const SfrSsnVerificationPage = React.lazy(() => import("@/components/pages/verification/SfrSsnVerificationPage"));
const SnilsValidationPage = React.lazy(() => import("@/components/pages/validation/SnilsValidationPage"));
const StaffLoginPage = React.lazy(() => import("@/components/pages/StaffLoginPage"));
const StaffOnboardingPage = React.lazy(() => import("@/components/pages/StaffOnboardingPage"));
const StaffPage = React.lazy(() => import("@/components/pages/admin/StaffPage"));
const DemoAccessPage = React.lazy(() => import("@/components/pages/admin/DemoAccessPage"));
const TaxDebtPage = React.lazy(() => import("@/components/pages/compliance/TaxDebtPage"));
const TaxIdValidationPage = React.lazy(() => import("@/components/pages/validation/TaxIdValidationPage"));
const TerroristExtremePage = React.lazy(() => import("@/components/pages/compliance/TerroristExtremePage"));
const TwoFaGuidePage = React.lazy(() => import("@/components/pages/help/TwoFaGuidePage"));
const ValidationPage = React.lazy(() => import("@/components/pages/ValidationPage"));
const VehicleCheckPage = React.lazy(() => import("@/components/pages/VehicleCheckPage"));

// Move pageComponents OUTSIDE the component to prevent recreation on every render
// This is critical - recreating this object causes component unmount/remount and state loss
//
// Теперь это внутренний резолвер маршрута /modules/:key — ключи менять нельзя,
// на них завязаны история проверок, уведомления и сохранённые ссылки.
// Тип как у карточек отчёта (reportBlocksConfig): LazyExoticComponent не
// подходит под ComponentType, и без этого типа карта не собирается.
const pageComponents: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  complex_check: PersonCheckPage,
  modules_catalog: ModulesCatalogPage,
  company_check: CompanyCheckPage,
  vehicle_check: VehicleCheckPage,
  crypto_check: CryptoCheckPage,
  debtor_wanted: FsspDebtorWantedPage,
  criminal_wanted: FsspCriminalWantedPage,
  fsin_criminal_wanted: FsinCriminalWantedPage,
  interpol_red_notice: InterpolRedNoticePage,
  interpol_un_sanctions: InterpolUnSanctionsPage,
  interpol_yellow_notice: InterpolYellowNoticePage,
  admin_disqualified: FnsDisqualifiedPage,
  au_violations: AUViolationsPage,
  tax_debt: TaxDebtPage,
  account_suspension: FnsAccountSuspensionPage,
  fns_subsidiary_defendant: FnsSubsidiaryDefendantPage,
  alimony_debt: AlimonyDebtPage,
  credit_rating: CreditRatingPage,
  credit_scoring: CreditScoringPage,
  execution_proceedings: ExecutionProceedingsPage,
  terrorist_extreme: TerroristExtremePage,
  corruption: CorruptionPage,
  foreign_agent: ForeignAgentPage,
  cbr_warning_list: CbrWarningListPage,
  fns_mass_leaders: FnsMassLeadersPage,
  fns_mass_founders: FnsMassFoundersPage,
  sanctions_screening: SanctionsScreeningPage,
  pep_screening: PepScreeningPage,
  pledge: FNPPledgesPage,
  bankruptcy: BankruptcyPage,
  general_courts: GeneralCourtsPage,
  arbitration_courts: ArbitrationCourtsPage,
  mvd_controlled_persons: MVDControlledPersonsPage,
  mvd_wanted: MvdWantedPage,
  fns_tax_id_verification: FnsTaxIdVerificationPage,
  passport_verification: PassportVerificationPage,
  mvd_driver_license_verification: MvdDriverLicenseVerificationPage,
  sfr_ssn_verification: SfrSsnVerificationPage,
  obrnadzor_education_verification: ObrnadzorEducationVerificationPage,
  validation: ValidationPage,
  address_validation: AddressValidationPage,
  phone_validation: PhoneValidationPage,
  email_validation: EmailValidationPage,
  name_validation: NameValidationPage,
  document_validation: DocumentValidationPage,
  tax_id_validation: TaxIdValidationPage,
  snils_validation: SnilsValidationPage,
  ip_address_validation: IPAddressValidationPage,
  digital_footprint: DigitalFootprintPage,
  osint_phone: OsintPhonePage,
  osint_email: OsintEmailPage,
  osint_username: OsintUsernamePage,
  osint_domain: OsintDomainPage,
  osint_ip: OsintIpPage,
  osint_crypto: OsintCryptoPage,
  history: HistoryPage,
  monitoring: MonitoringPage,
  monitoring_run_report: MonitoringRunReportPage,
  monitoring_subject_profile: MonitoringSubjectProfilePage,
  extended_report: ExtendedReportPage,
  settings: SettingsPage,
  presentation: PresentationPage,
  administrative_violations: AdministrativeViolationsPage,
  health_dashboard: HealthDashboardPage,
  complex_report: ComplexCheckReportPage,
  fetcher_dashboard: FetcherDashboardPage,
  company_report: CompanyReportPage,
  complex_legal_report: ComplexLegalCheckReportPage,
  clients_list: ClientsListPage,
  applications_list: ApplicationsListPage,
  organization_dashboard: OrganizationDashboardPage,
  organization_billing: OrganizationBillingPage,
  organization_checkout: OrganizationCheckoutPage,
  billing_settings: BillingSettingsPage,
  admin_invoices: AdminInvoicesPage,
  admin_subscriptions: AdminSubscriptionsPage,
  platform_risk_matrices: PlatformRiskMatrixPage,
  staff_page: StaffPage,
  demo_access: DemoAccessPage,
  notifications_page: NotificationsPage,
  notifications_preferences: NotificationsPreferencesPage,
};

/** Комплексный отчёт физлица: job_id берём из адреса /reports/:jobId. */
const PersonReportRoute: React.FC = () => {
  const { jobId } = useParams();
  return <ComplexCheckReportPage jobId={jobId} />;
};

/** Отчёт по юрлицу: job_id из адреса /reports/legal/:jobId. */
const LegalReportRoute: React.FC = () => {
  const { jobId } = useParams();
  return <ComplexLegalCheckReportPage jobId={jobId} />;
};

/** Заглушка на время загрузки чанка страницы. */
const PageFallback: React.FC = () => (
  <div className="flex h-full min-h-[60vh] items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

/**
 * Страница, скрытая в демо (DEMO_HIDDEN_PAGES): демо-клиента уводим на
 * стартовую. Пока статус демо не загружен, страница рендерится как обычно —
 * её API для демо всё равно закрыт на бэкенде.
 */
const DemoHidden: React.FC<{ page: string; children: React.ReactElement }> = ({ page, children }) => {
  const demo = useDemoStatus();
  if (demo?.is_demo && DEMO_HIDDEN_PAGES.has(page)) return <Navigate to={DEFAULT_PATH} replace />;
  return children;
};

/** Страницы отдельных модулей: /modules/:pageKey → pageComponents[pageKey]. */
const ModulePageRoute: React.FC = () => {
  const { pageKey = "" } = useParams();

  // У сценарных экранов есть собственный адрес: не даём одной странице жить
  // по двум ссылкам — уводим на канонический адрес.
  const canonical = canonicalPathForModuleKey(pageKey);
  if (canonical) return <Navigate to={canonical} replace />;

  // Модуль, для которого своей страницы не написали, открывается формой по
  // паспорту из реестра: ключ модуля узнаётся по точкам («compliance.kaz.
  // wanted»), ключ страницы их не содержит. Иначе такие модули — а это все
  // проверки, кроме российских, — оставались бы недоступны из интерфейса.
  const PageComponent = pageComponents[pageKey];
  if (!PageComponent) {
    if (pageKey.includes(".")) return <GenericModulePage moduleKey={pageKey} />;
    return <Navigate to={DEFAULT_PATH} replace />;
  }

  return <PageComponent />;
};

/**
 * Содержимое рабочей области. Ключ анимации — ключ страницы, а не полный
 * адрес: переход /reports/a → /reports/b не должен перезапускать анимацию
 * и перемонтировать отчёт.
 */
const AppRoutes: React.FC<{ activePage: string }> = ({ activePage }) => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      {/* Переход короткий и без сдвига: страница не должна «проявляться» —
          ожидание данных показывают скелетоны внутри готовой раскладки. */}
      <motion.div
        key={activePage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12 }}
        className="h-full"
      >
        {/* location передаём явно: AnimatePresence держит уходящую копию ещё
            200 мс, и без снимка адреса она успела бы отрисовать в себе уже
            новую страницу — лишний маунт и второй SSE-коннект у отчёта. */}
        {/* Страницы подгружаются по требованию, и без заглушки переход
            показывал пустое место: у React.lazy до готовности чанка нет
            ничего. Спиннер в той же раскладке — чтобы не прыгала высота. */}
        <Suspense fallback={<PageFallback />}>
          <Routes location={location}>
            <Route index element={<Navigate to={DEFAULT_PATH} replace />} />

            <Route path="check/person" element={<PersonCheckPage />} />
            <Route
              path="check/org"
              element={
                <DemoHidden page="company_check">
                  <CompanyCheckPage />
                </DemoHidden>
              }
            />
            <Route
              path="check/vehicle"
              element={
                <DemoHidden page="vehicle_check">
                  <VehicleCheckPage />
                </DemoHidden>
              }
            />
            <Route path="check/crypto" element={<CryptoCheckPage />} />
            <Route path="fizur" element={<FizurPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="monitoring" element={<MonitoringPage />} />

            {/* Статический сегмент legal ранжируется выше :jobId, поэтому
              /reports/legal/... не попадёт в отчёт по физлицу. */}
            <Route path="reports/legal" element={<LegalReportRoute />} />
            <Route path="reports/legal/:jobId" element={<LegalReportRoute />} />
            <Route path="reports" element={<PersonReportRoute />} />
            <Route path="reports/:jobId" element={<PersonReportRoute />} />

            {/* Каталог проверок — витрина всего реестра, включая международные
              модули: их нет в жёстком списке бокового меню. */}
            <Route path="modules" element={<ModulesCatalogPage />} />
            <Route path="modules/:pageKey" element={<ModulePageRoute />} />

            <Route path="*" element={<Navigate to={DEFAULT_PATH} replace />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Внутренний контент приложения (защищённый).
 */
const AppContent: React.FC = () => {
  const activePage = useActivePageKey();
  const navigate = useNavigate();
  const loading = useLoading();
  const darkMode = useDarkMode();
  const { setSystemInfo, addNotification, setDarkMode, setCurrentPageFromLocation } = useAppStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toggleHover, setToggleHover] = useState(false);

  // Мост «стор → роутер»: setCurrentPage зовут из кода вне роутера, и он
  // должен менять адрес, иначе ссылки и «назад» снова перестанут работать.
  useEffect(() => {
    setRouterNavigate((path) => {
      // Повторный клик по уже открытому пункту меню не должен плодить записи
      // в истории браузера — иначе «назад» пришлось бы жать несколько раз.
      if (path === `${window.location.pathname}${window.location.search}`) return;
      navigate(path);
    });
    return () => setRouterNavigate(null);
  }, [navigate]);

  // Мост «роутер → стор»: адрес (в том числе F5 и «назад») задаёт currentPage.
  useEffect(() => {
    setCurrentPageFromLocation(activePage);
  }, [activePage, setCurrentPageFromLocation]);

  // Инициализация приложения
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Проверяем работоспособность API
        await apiService.healthCheck();

        // Получаем информацию о системе
        const systemInfo = await apiService.getCapabilities();
        // Успех не объявляем тостом: живое состояние — индикатор «Все системы»
        // в подвале. Тост на каждой загрузке перекрывал панель поиска.
        setSystemInfo(systemInfo);
      } catch (error) {
        console.error("Ошибка инициализации:", error);
        addNotification({
          type: "error",
          title: "Ошибка подключения",
          message: "Не удается подключиться к API. Проверьте подключение к серверу.",
          duration: 0, // Не скрывать автоматически
        });
      }
    };

    initializeApp();
  }, [addNotification, setSystemInfo]);

  // Тема: явный выбор пользователя переживает перезагрузку, «system» следует
  // за устройством. Раньше выбор не сохранялся вовсе, и после перехода по
  // адресу тема возвращалась к системной.
  const systemThemeIsDark = useSystemTheme();
  const themePreference = useAppStore((state) => state.themePreference);

  useEffect(() => {
    setDarkMode(themePreference === "system" ? systemThemeIsDark : themePreference === "dark");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemThemeIsDark, themePreference]);

  // Применение темной темы к DOM
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  return (
    <ProtectedRoute>
      <div className={cn("min-h-screen w-full bg-surface-topbar relative", "transition-colors duration-200")}>
        {/* Глобальный спиннер загрузки */}
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-surface-canvas/70 z-50 flex items-center justify-center"
            >
              <div className="bg-surface-elevated border border-line p-8 rounded-card shadow-lg">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-ink-2 text-center">Выполняется проверка...</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Верхняя панель */}
        <TopBar onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

        {/* Боковая панель */}
        <Sidebar
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
          collapsed={sidebarCollapsed}
          breathing={toggleHover}
        />

        {/* Stripe-style collapse handle на границе sidebar */}
        <SidebarToggle
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          onHoverChange={setToggleHover}
        />

        {/* Основной контент — карточка из-под тёмного topbar со скруглением сверху-справа.
            Padding-left синхронизирован с шириной sidebar (включая breathing на hover). */}
        <main
          className="pt-[52px] pb-10 min-h-screen text-ink-1 lg:pl-[var(--sb-w)]"
          style={
            {
              "--sb-w": `${sidebarCollapsed ? (toggleHover ? 80 : 64) : toggleHover ? 244 : 256}px`,
              transition: "padding-left 220ms cubic-bezier(0.4, 0, 0.2, 1)",
            } as React.CSSProperties
          }
        >
          <div
            className={cn(
              "bg-surface-canvas lg:rounded-tr-panel",
              "h-[calc(100vh-92px)] overflow-hidden flex flex-col",
            )}
          >
            <DemoBanner />
            <div
              className={cn(
                "flex-1 overflow-y-auto overflow-x-hidden",
                "[scrollbar-gutter:stable]",
                "[&::-webkit-scrollbar]:w-2.5",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:border-[3px] [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:rounded-full",
                "hover:[&::-webkit-scrollbar-thumb]:bg-line-strong [&::-webkit-scrollbar-thumb]:bg-clip-padding",
              )}
            >
              <div
                className={cn(
                  "mx-auto px-8 pt-8 pb-12",
                  // Отчёт 360° использует свою колонку с оглавлением слева —
                  // ему нужна более широкая обёртка, остальные страницы не трогаем.
                  activePage === PERSON_REPORT_PAGE ? "max-w-[1180px]" : "max-w-5xl",
                )}
              >
                <PageBar />
                <NoBalanceBanner />
                <AppRoutes activePage={activePage} />
              </div>
            </div>
          </div>
        </main>

        {/* Глобальный footer */}
        <Footer />

        {/* Менеджер уведомлений */}
        <NotificationManager />

        {/* Toaster для дополнительных уведомлений */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "rgb(var(--c-elevated))",
              color: "rgb(var(--c-ink-1))",
              border: "1px solid rgb(var(--c-border))",
              borderRadius: "10px",
              fontSize: "13px",
            },
            success: {
              iconTheme: {
                primary: "#15803d",
                secondary: "#ffffff",
              },
            },
            error: {
              iconTheme: {
                primary: "#b91c1c",
                secondary: "#ffffff",
              },
            },
          }}
        />
      </div>
    </ProtectedRoute>
  );
};

/**
 * Главный компонент приложения с маршрутизацией.
 */
export const App: React.FC = () => {
  const location = useLocation();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes location={location}>
          {/* Публичные маршруты (без аутентификации) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<ClientRegisterPage />} />
          <Route path="/staff" element={<StaffLoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/help/2fa" element={<TwoFaGuidePage />} />
          <Route path="/complete-registration" element={<CompleteRegistrationPage />} />
          <Route path="/staff-access" element={<StaffOnboardingPage />} />

          {/* Основное приложение (защищённые маршруты). Поддомен fizur.* —
              одна страница физюра в облегчённой оболочке. */}
          <Route path="/*" element={isFizurHost() ? <FizurShell /> : <AppContent />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
};
